import {ApolloClient, InMemoryCache, gql} from '@apollo/client'

const client = new ApolloClient({
  uri: 'https://waterlogic-de.myshopify.com/api/2024-01/graphql.json',
  cache: new InMemoryCache(),
  headers: {
    'X-Shopify-Storefront-Access-Token': '93a39e3e071c529bc8c8dc15c9cb5a5f',
  },
})

// GraphQL mutations and queries
const CREATE_CART = gql`
  mutation createCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
              }
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  sku
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        discountCodes {
          applicable
          code
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

const GET_CART = gql`
  query getCart($cartId: ID!) {
    cart(id: $cartId) {
      id
      checkoutUrl
      totalQuantity
      cost {
        subtotalAmount {
          amount
          currencyCode
        }
        totalAmount {
          amount
          currencyCode
        }
        totalTaxAmount {
          amount
          currencyCode
        }
      }
      lines(first: 100) {
        edges {
          node {
            id
            quantity
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
            }
            merchandise {
              ... on ProductVariant {
                id
                title
                sku
                price {
                  amount
                  currencyCode
                }
                product {
                  title
                  handle
                  images(first: 1) {
                    edges {
                      node {
                        url
                        altText
                        width
                        height
                      }
                    }
                  }
                }
              }
            }
            attributes {
              key
              value
            }
          }
        }
      }
      discountCodes {
        applicable
        code
      }
    }
  }
`

const ADD_TO_CART = gql`
  mutation addToCart($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
              }
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  sku
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        discountCodes {
          applicable
          code
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

const UPDATE_CART_LINE = gql`
  mutation updateCartLine($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
              }
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  sku
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        discountCodes {
          applicable
          code
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

const REMOVE_FROM_CART = gql`
  mutation removeFromCart($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
              }
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  sku
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        discountCodes {
          applicable
          code
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

const APPLY_DISCOUNT = gql`
  mutation applyDiscount($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        checkoutUrl
        totalQuantity
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
          totalTaxAmount {
            amount
            currencyCode
          }
        }
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
              }
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  sku
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        discountCodes {
          applicable
          code
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

export interface CartLineInput {
  merchandiseId: string
  quantity: number
  attributes?: Array<{key: string; value: string}>
}

export interface CartLineUpdateInput {
  id: string
  quantity: number
}

export class CartApiService {
  async createCart(lines?: CartLineInput[]) {
    try {
      const {data} = await client.mutate({
        mutation: CREATE_CART,
        variables: {
          input: {
            lines: lines || [],
          },
        },
      })

      console.log('Raw API response:', data)

      if (data.cartCreate.userErrors.length > 0) {
        throw new Error(data.cartCreate.userErrors[0].message)
      }

      if (!data.cartCreate.cart) {
        throw new Error('Cart creation failed - no cart data returned')
      }

      console.log('Cart created successfully:', data.cartCreate.cart)
      return this.transformCartData(data.cartCreate.cart)
    } catch (error) {
      console.error('Error creating cart:', error)
      throw error
    }
  }

  async getCart(cartId: string) {
    try {
      const {data} = await client.query({
        query: GET_CART,
        variables: {cartId},
      })

      return this.transformCartData(data.cart)
    } catch (error) {
      console.error('Error fetching cart:', error)
      throw error
    }
  }

  async addToCart(cartId: string, lines: CartLineInput[]) {
    try {
      const {data} = await client.mutate({
        mutation: ADD_TO_CART,
        variables: {
          cartId,
          lines,
        },
      })

      if (data.cartLinesAdd.userErrors.length > 0) {
        throw new Error(data.cartLinesAdd.userErrors[0].message)
      }

      return this.transformCartData(data.cartLinesAdd.cart)
    } catch (error) {
      console.error('Error adding to cart:', error)
      throw error
    }
  }

  async updateCartLine(cartId: string, lines: CartLineUpdateInput[]) {
    try {
      const {data} = await client.mutate({
        mutation: UPDATE_CART_LINE,
        variables: {
          cartId,
          lines,
        },
      })

      if (data.cartLinesUpdate.userErrors.length > 0) {
        throw new Error(data.cartLinesUpdate.userErrors[0].message)
      }

      return this.transformCartData(data.cartLinesUpdate.cart)
    } catch (error) {
      console.error('Error updating cart line:', error)
      throw error
    }
  }

  async removeFromCart(cartId: string, lineIds: string[]) {
    try {
      const {data} = await client.mutate({
        mutation: REMOVE_FROM_CART,
        variables: {
          cartId,
          lineIds,
        },
      })

      if (data.cartLinesRemove.userErrors.length > 0) {
        throw new Error(data.cartLinesRemove.userErrors[0].message)
      }

      return this.transformCartData(data.cartLinesRemove.cart)
    } catch (error) {
      console.error('Error removing from cart:', error)
      throw error
    }
  }

  async applyDiscount(cartId: string, discountCode: string) {
    try {
      const {data} = await client.mutate({
        mutation: APPLY_DISCOUNT,
        variables: {
          cartId,
          discountCodes: [discountCode],
        },
      })

      if (data.cartDiscountCodesUpdate.userErrors.length > 0) {
        throw new Error(data.cartDiscountCodesUpdate.userErrors[0].message)
      }

      return this.transformCartData(data.cartDiscountCodesUpdate.cart)
    } catch (error) {
      console.error('Error applying discount:', error)
      throw error
    }
  }

  private transformCartData(cartData: any) {
    if (!cartData) return null

    return {
      id: cartData.id,
      checkoutUrl: cartData.checkoutUrl,
      totalQuantity: cartData.totalQuantity || 0,
      subtotalPrice: cartData.cost?.subtotalAmount?.amount || '0.00',
      totalPrice: {
        amount: cartData.cost?.totalAmount?.amount || '0.00',
        currencyCode: cartData.cost?.totalAmount?.currencyCode || 'EUR',
      },
      totalPriceV2: {
        amount: cartData.cost?.totalAmount?.amount || '0.00',
        currencyCode: cartData.cost?.totalAmount?.currencyCode || 'EUR',
      },
      subtotalPriceV2: {
        amount: cartData.cost?.subtotalAmount?.amount || '0.00',
        currencyCode: cartData.cost?.subtotalAmount?.currencyCode || 'EUR',
      },
      totalTaxV2: {
        amount: cartData.cost?.totalTaxAmount?.amount || '0.00',
        currencyCode:
          cartData.cost?.totalTaxAmount?.currencyCode ||
          cartData.cost?.totalAmount?.currencyCode ||
          'EUR',
      },
      lineItems: (cartData.lines?.edges || []).map((edge: any) => ({
        id: edge.node.id,
        quantity: edge.node.quantity,
        title: edge.node.merchandise?.product?.title || '',
        customAttributes: edge.node.attributes || [],
        variant: {
          sku: edge.node.merchandise?.sku || '',
          title: edge.node.merchandise?.title || '',
          product: {
            handle: edge.node.merchandise?.product?.handle || '',
          },
          price: {
            amount: edge.node.merchandise?.price?.amount || '0.00',
            currencyCode: edge.node.merchandise?.price?.currencyCode || 'EUR',
          },
          priceV2: {
            amount: edge.node.merchandise?.price?.amount || '0.00',
            currencyCode: edge.node.merchandise?.price?.currencyCode || 'EUR',
          },
          image: edge.node.merchandise?.product?.images?.edges[0]?.node || {
            src: '',
            height: 0,
            width: 0,
            altText: '',
          },
        },
        cost: edge.node.cost?.subtotalAmount || {
          amount: '0.00',
          currencyCode: 'EUR',
        },
      })),
      discountApplications: (cartData.discountCodes || []).map(
        (discount: any) => ({
          applicable: discount.applicable || false,
          code: discount.code || '',
          value: {
            percentage: 0, // This would need to be calculated based on the discount type
          },
        }),
      ),
    }
  }
}

export const cartApiService = new CartApiService()
